import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Course } from '../types';
import { adminAPI } from '../services/api';
import CourseManagement from '../components/admin/CourseManagement';
import LessonManagement from '../components/admin/LessonManagement';
import AdminStats from '../components/admin/AdminStats';

const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'courses' | 'lessons' | 'stats'>('courses');
  const [courses, setCourses] = useState<Course[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const coursesData = await adminAPI.getAllCourses();
      setCourses(coursesData);
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCourseCreated = (newCourse: Course) => {
    setCourses([...courses, newCourse]);
  };

  const handleCourseUpdated = (updatedCourse: Course) => {
    setCourses(courses.map(course => 
      course.id === updatedCourse.id ? updatedCourse : course
    ));
    if (selectedCourse?.id === updatedCourse.id) {
      setSelectedCourse(updatedCourse);
    }
  };

  const handleCourseDeleted = (courseId: number) => {
    setCourses(courses.filter(course => course.id !== courseId));
    if (selectedCourse?.id === courseId) {
      setSelectedCourse(null);
    }
  };

  const handleManageLessons = (course: Course) => {
    setSelectedCourse(course);
    setActiveTab('lessons');
  };

  // Check if user is admin
  if (!user || (!user.is_admin && user.role !== 'admin' && user.role !== 'super_admin')) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to access the admin dashboard.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-gray-600 mt-2">Manage courses, lessons, and platform content</p>
        </div>

        {/* Navigation Tabs */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('courses')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'courses'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Course Management
            </button>
            <button
              onClick={() => setActiveTab('lessons')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'lessons'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Lesson Management
            </button>
            <button
              onClick={() => setActiveTab('stats')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'stats'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Platform Statistics
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'courses' && (
          <CourseManagement
            courses={courses}
            onCourseCreated={handleCourseCreated}
            onCourseUpdated={handleCourseUpdated}
            onCourseDeleted={handleCourseDeleted}
            onCourseSelected={setSelectedCourse}
            onManageLessons={handleManageLessons}
            selectedCourse={selectedCourse}
          />
        )}

        {activeTab === 'lessons' && (
          <LessonManagement
            courses={courses}
            selectedCourse={selectedCourse}
            onCourseSelected={setSelectedCourse}
            onCourseUpdated={handleCourseUpdated}
          />
        )}

        {activeTab === 'stats' && (
          <AdminStats />
        )}
      </div>
    </div>
  );
};

export default AdminDashboard; 